import TimelineEvent from '../domain/models/TimelineEvent';

export default class TimelineService {
  constructor({ attendanceService, incidentService, inventoryService, permissions }) {
    this.attendanceService = attendanceService;
    this.incidentService = incidentService;
    this.inventoryService = inventoryService;
    this.permissions = permissions;
  }

  buildForStudent(student, permissionsOverride = null) {
    if (!student) return [];

    const permissions = permissionsOverride || this.permissions;

    const baseEvents = [
      ...student.safeguardingEvents
        .filter((event) => (permissions ? permissions.canViewEvent(event) : true))
        .map((event) =>
          new TimelineEvent({
            id: `saf-${event.id}`,
            category: 'Safeguarding',
            date: event.date,
            description: event.description,
            severity: event.severity,
            sourceId: event.id
          })
        ),
      ...student.medicalEvents
        .filter((event) => (permissions ? permissions.canViewEvent(event) : true))
        .map((event) =>
          new TimelineEvent({
            id: `med-${event.id}`,
            category: 'Medical',
            date: event.date,
            description: event.description,
            severity: event.severity,
            sourceId: event.id
          })
        ),
      ...student.policeEvents
        .filter((event) => (permissions ? permissions.canViewEvent(event) : true))
        .map((event) =>
          new TimelineEvent({
            id: `pol-${event.id}`,
            category: 'Police',
            date: event.date,
            description: event.description,
            severity: event.severity,
            sourceId: event.id
          })
        )
    ];

    const attendanceEvents = (this.attendanceService?.buildAttendanceTimeline(student) || []).map((event) =>
      new TimelineEvent({
        id: `att-${event.id}`,
        category: 'Attendance',
        date: event.date,
        description: event.description,
        severity: event.severity,
        sourceId: event.id
      })
    );

    const incidentEvents = (this.incidentService?.listByStudent(student.id) || []).map((incident) =>
      new TimelineEvent({
        id: `inc-${incident.id}`,
        category: 'Incident',
        date: incident.date,
        description: `${incident.type}${incident.description ? ` · ${incident.description}` : ''}`,
        severity: incident.severity || 'medium',
        sourceId: incident.id
      })
    );

    const inventoryAssignments = this.inventoryService?.getAssignmentsForStudent(student.id) || [];
    const inventoryEvents = inventoryAssignments.flatMap(({ item, assignment, due }) => {
      const events = [
        new TimelineEvent({
          id: `inv-${item.id}-${assignment.id}-out`,
          category: 'Inventory',
          date: assignment.checkoutDate,
          description: `Item assigned: ${item.name}`,
          severity: 'low',
          sourceId: item.id
        })
      ];

      if (assignment.checkinDate) {
        events.push(
          new TimelineEvent({
            id: `inv-${item.id}-${assignment.id}-in`,
            category: 'Inventory',
            date: assignment.checkinDate,
            description: `Item returned: ${item.name}`,
            severity: 'low',
            sourceId: item.id
          })
        );
      }

      if (due?.isOverdue) {
        events.push(
          new TimelineEvent({
            id: `inv-${item.id}-${assignment.id}-overdue`,
            category: 'Inventory',
            date: assignment.dueDate || assignment.checkoutDate,
            description: `Overdue item: ${item.name}`,
            severity: 'high',
            sourceId: item.id
          })
        );
      }

      return events;
    });

    const statusEvents = (student.statusHistory || []).map((event, idx) =>
      new TimelineEvent({
        id: `status-${idx}`,
        category: 'Status',
        date: event.date,
        description: `Status changed to ${event.status} by ${event.by}`,
        severity: event.status === 'Under Review' ? 'medium' : 'low',
        sourceId: event.status
      })
    );

    return [...baseEvents, ...attendanceEvents, ...incidentEvents, ...inventoryEvents, ...statusEvents]
      .filter((event) => event.date)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }
}
